import { isTauriRuntime } from "@/lib/platform";
import { WEB_URL } from "@/lib/runtime-config";

/**
 * Links to legal pages (privacy, terms, account deletion).
 *
 * ⚠️ **Why this is not just `<a href="/privacy.html" target="_blank">`.**
 *
 * Google Play requires the privacy policy to be linked **from inside the
 * app**, not only on the store listing. In the native binary both things
 * fail simultaneously:
 *
 *  1. `target="_blank"` does not open a new tab: there is no surrounding browser. In
 *     the best case WebView navigates in the same window and user is left
 *     inside a static page without navigation bar; in the worst, nothing
 *     happens at all.
 *  2. Even if it navigated, `/privacy.html` is relative to app origin, which in
 *     Tauri is `tauri://localhost` — not the public web. The URL that store has
 *     reviewed is `app.cashsync.app`, and that is the one that must open.
 *
 * **It is a silent non-compliance**: no error, no log, no broken
 * screen. Simply the reviewer taps "Privacy" and nothing happens.
 *
 * The solution is the same one this repo already uses for social OAuth: in Tauri we
 * open the **system browser** with the absolute public URL; on web we continue
 * using the usual relative path.
 */
/** Arguments for `window.open`: target name and features. Not UI text. */
const OPEN_TARGET = "_blank";
// oxlint-disable-next-line lingui/no-unlocalized-strings -- lista de features de window.open, no texto de UI
const OPEN_FEATURES = "noopener,noreferrer";

export const LEGAL_PAGES = {
  privacy: "/privacy.html",
  terms: "/terms.html",
  deleteAccount: "/delete-account.html",
} as const;

export type LegalPage = keyof typeof LEGAL_PAGES;

/**
 * Link `href`. On web, relative (historic behavior). In native,
 * absolute public URL, so copying link or opening with system
 * leads to the page the store reviewed.
 */
export function legalHref(page: LegalPage): string {
  const path = LEGAL_PAGES[page];
  return isTauriRuntime() ? `${WEB_URL.replace(/\/+$/u, "")}${path}` : path;
}

/**
 * Opens a legal page. Returns `true` if handled (and therefore
 * browser must NOT follow `href`), `false` if link should proceed
 * normally.
 *
 * Fails toward normal link: if Tauri plugin is absent or fails,
 * returns `false` and `<a>` takes its course. We never leave user without
 * any way to reach the policy.
 */
export async function openLegalPage(page: LegalPage): Promise<boolean> {
  if (!isTauriRuntime()) {
    return false;
  }
  try {
    // Dynamic import: keeps `@tauri-apps/plugin-opener` out of web bundle.
    const { openUrl } = await import("@tauri-apps/plugin-opener");
    await openUrl(legalHref(page));
    return true;
  } catch {
    return false;
  }
}

/**
 * Click handler for a legal `<a>`.
 *
 * ⚠️ `preventDefault()` must occur **synchronously**: after an
 * `await` the event has already propagated and cancelling does nothing. Therefore
 * cancel first (only in native, where `href` leads nowhere useful)
 * and only then attempt to open. If opening fails, fallback to `window.open`
 * to avoid leaving policy unreachable.
 */
export function handleLegalLinkClick(event: MouseEvent, page: LegalPage): void {
  if (!isTauriRuntime()) {
    return; // web: the <a> does the right thing on its own.
  }
  event.preventDefault();
  void (async () => {
    const opened = await openLegalPage(page);
    if (!opened) {
      globalThis.open(legalHref(page), OPEN_TARGET, OPEN_FEATURES);
    }
  })();
}
